import React from 'react';
import SkeletonRow from './SkeletonRow';
import './Skeleton.css';

/**
 * TableSkeleton Component
 * 
 * Container component - renders complete table skeleton
 * Responsibility: Compose multiple SkeletonRows into full table structure
 * Reusable: Works with any table configuration
 * 
 * Principles:
 * - Component-Based Architecture: Self-contained loading state
 * - Composition: Built from SkeletonRow components
 * - Unidirectional Data Flow: Props flow down (columns, rowCount)
 * - Separation of Concerns: Only handles table structure, not individual cells
 * - DRY: Reusable for any data table
 * 
 * @param {Array} columns - Column configuration for skeleton cells
 * @param {number} rowCount - Number of skeleton rows to display
 * @param {string} tableClassName - CSS class for table styling
 */
export default function TableSkeleton({ 
  columns = [],
  rowCount = 10,
  tableClassName = 'admin-users-table',
  loading = true
}) {
  // Generate exact array matching items per page
  const rows = Array.from({ length: rowCount }, (_, i) => i);

  // Map columns to skeleton widths with visual hierarchy
  const getSkeletonConfig = (column) => {
    // Match real content widths for believability
    const widthMap = {
      'NAME': { width: '70%', height: '16px' },      // Longer text
      'EMAIL': { width: '85%', height: '14px' },     // Medium text
      'ROLE': { width: '50%', height: '14px' },      // Short text
      'STATUS': { width: '60%', height: '20px' },    // Badge-like
      'REGISTERED': { width: '65%', height: '14px' }, // Date format
      'ACTIONS': { width: '75%', height: '18px' },   // Buttons
    };

    return widthMap[column.label] || { width: '70%', height: '16px' };
  };

  return (
    <div className="skeleton-table-wrapper">
      <table className={tableClassName}>
        <thead className={loading ? 'loading-state' : ''}>
          <tr>
            {columns.map((column, index) => (
              <th 
                key={index} 
                style={column.headerStyle}
                className="skeleton-header"
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((rowIndex) => (
            <SkeletonRow 
              key={rowIndex} 
              columns={columns.map(col => ({
                ...getSkeletonConfig(col),
                style: col.cellStyle
              }))}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
